import { useState, useEffect, useCallback } from 'react';
import { useBlocker } from '@tanstack/react-router';

export interface UsePageUnsavedChangesOptions {
  /**
   * Whether the page has unsaved changes
   */
  hasUnsavedChanges: boolean;

  /**
   * Optional callback to execute when navigating away (after confirmation)
   */
  onNavigateAway?: () => void;
}

/**
 * Manages unsaved changes warning for page-level components
 *
 * Handles all navigation scenarios:
 * 1. Browser close/refresh - shows native browser warning
 * 2. Browser back/forward navigation - shows custom confirmation dialog
 * 3. Navigation via router links (sidebar, etc.) - blocks and shows custom dialog
 * 4. Programmatic navigation - use handleNavigateWithConfirmation() to check before navigating
 *
 * @example
 * const { showConfirmation, handleNavigateWithConfirmation, handleStay, handleDiscard } =
 *   usePageUnsavedChanges({
 *     hasUnsavedChanges: drafts.length > 0,
 *     onNavigateAway: () => navigate('/somewhere')
 *   });
 *
 * // In your component:
 * <Button onClick={handleNavigateWithConfirmation}>Cancel</Button>
 * <UnsavedChangesConfirmDialog
 *   open={showConfirmation}
 *   onStay={handleStay}
 *   onDiscard={handleDiscard}
 * />
 */
export function usePageUnsavedChanges({
  hasUnsavedChanges,
  onNavigateAway
}: UsePageUnsavedChangesOptions) {
  const [showConfirmation, setShowConfirmation] = useState(false);

  /**
   * Use TanStack Router's blocker to intercept navigation (sidebar links,
   * browser back/forward, etc.). Contract: this hook guards LEAVING the page —
   * same-pathname transitions (search params, e.g. a wizard's ?step=) are the
   * page's own responsibility, since its state survives them.
   */
  const { proceed, reset, status } = useBlocker({
    shouldBlockFn: ({ current, next }) => hasUnsavedChanges && current.pathname !== next.pathname,
    withResolver: true,
    // A plain `true` prompts on reload even without unsaved changes.
    enableBeforeUnload: () => hasUnsavedChanges
  });

  useEffect(
    function showConfirmationWhenBlocked() {
      if (status === 'blocked') {
        setShowConfirmation(true);
      }
    },
    [status]
  );

  /**
   * Handle programmatic navigation with confirmation
   */
  const handleNavigateWithConfirmation = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowConfirmation(true);
    } else {
      onNavigateAway?.();
    }
  }, [hasUnsavedChanges, onNavigateAway]);

  /**
   * Handle staying on the page (cancel navigation)
   */
  const handleStay = useCallback(() => {
    setShowConfirmation(false);
    // Reset the blocker to cancel the navigation attempt
    if (status === 'blocked') {
      reset();
    }
  }, [status, reset]);

  /**
   * Handle discarding changes and navigating away
   */
  const handleDiscard = useCallback(() => {
    setShowConfirmation(false);
    // If blocked by router, proceed with navigation
    if (status === 'blocked') {
      proceed();
    } else {
      // For programmatic navigation (cancel button)
      onNavigateAway?.();
    }
  }, [status, proceed, onNavigateAway]);

  return {
    showConfirmation,
    handleNavigateWithConfirmation,
    handleStay,
    handleDiscard
  };
}
